import React, { useState, useEffect } from "react";
import "./AdminSettings.css";
import bfLogo from "../assets/bf_logo.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trash2, RefreshCw, Unlock, Pencil, Check } from "lucide-react";

/* ===== FIREBASE ===== */
import { auth, db } from "../firebase";
import { initializeApp, deleteApp } from "firebase/app"; 
import { getAuth, createUserWithEmailAndPassword, updatePassword, signOut as authSignOut, signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, collection, query, where, onSnapshot, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Reikalinga antriniam prisijungimui (kuriant adminą)
const firebaseConfig = auth.app.options;

function AdminSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [ownerData, setOwnerData] = useState(null); 
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  // REDAGAVIMO BŪSENA
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const isSubAdmin = userData?.belongsTo ? true : false;

  // Pridėjimo formos duomenys
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminName, setNewAdminName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // --- LIMITŲ LOGIKA (PATAISYTA) ---
  // Paimame limitą TIESIOGIAI iš DB. Jokio dirbtinio padidinimo iki 2.
  // Jei nėra limito (undefined), duodame default 1 (tik savininkas).
  const currentAdminLimit = ownerData?.adminLimit || 1;
  const currentWorkerLimit = ownerData?.workerLimit || 5;

  useEffect(() => {
    let unsubTeam = () => {};
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const uData = userSnap.data();
            setUserData(uData);

            const masterId = uData.belongsTo || user.uid;

            const ownerRef = doc(db, "users", masterId);
            const ownerSnap = await getDoc(ownerRef);
            if (ownerSnap.exists()) {
              const oData = ownerSnap.data();
              setOwnerData(oData);
              setEditFormData({
                ...oData,
                address: oData.address || { street: "", zip: "", city: "" }
              });
            }

            const q = query(collection(db, "users"), where("belongsTo", "==", masterId));
            unsubTeam = onSnapshot(q, (snap) => {
              setTeam(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
          }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
      } else {
        navigate("/admin/login");
      }
    });
    return () => { unsubscribeAuth(); unsubTeam(); };
  }, [navigate]);

  const handleSaveProfile = async () => {
    if (isSubAdmin) return;
    try {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const updateData = {
        name: editFormData.name || "",
        phone: editFormData.phone || "",
        address: editFormData.address
      };
      await updateDoc(userRef, updateData);
      setOwnerData({ ...ownerData, ...updateData });
      setIsEditingProfile(false);
      alert("✅ " + t('saved'));
    } catch (e) { alert(t('error')); }
  };

  const generateRandomPassword = () => Math.random().toString(36).slice(-8);

  // --- ADMINO KŪRIMO FUNKCIJA ---
  const handleCreateAdminAccess = async () => {
    if (isSubAdmin) return;
    
    const generatedPassword = generateRandomPassword();
    setIsCreating(true);
    
    try {
      const appName = "App_" + Date.now();
      const secondaryApp = initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuth(secondaryApp);
      
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newAdminEmail, generatedPassword);
      
      await setDoc(doc(db, "users", userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: newAdminEmail.toLowerCase(),
        name: newAdminName,
        role: "admin", 
        belongsTo: auth.currentUser.uid, 
        masterId: auth.currentUser.uid,
        plainPassword: generatedPassword, 
        loginAttempts: 0, 
        isLocked: false,
        subscriptionStatus: "active_sub",
        createdAt: Date.now()
      });

      await authSignOut(secondaryAuth);
      await deleteApp(secondaryApp);
      
      setNewAdminEmail(""); 
      setNewAdminName("");
      alert(`✅ Admin created! Password: ${generatedPassword}`);
    } catch (e) { 
      alert("Error creating admin: " + e.message); 
    }
    setIsCreating(false);
  };

  const handleRefresh = async (member) => {
    if (isSubAdmin) return;
    const newPass = generateRandomPassword();
    try {
      const appName = "Ref_" + Date.now();
      const secondaryApp = initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuth(secondaryApp);
      const userCredential = await signInWithEmailAndPassword(secondaryAuth, member.email, member.plainPassword);
      await updatePassword(userCredential.user, newPass);
      await updateDoc(doc(db, "users", member.id), { plainPassword: newPass, isLocked: false, loginAttempts: 0 });
      await authSignOut(secondaryAuth);
      await deleteApp(secondaryApp);
      alert(`✅ New Password: ${newPass}`);
    } catch (e) { alert(t('error')); }
  };

  const handleUnlock = async (member) => {
    if (isSubAdmin) return;
    try { await updateDoc(doc(db, "users", member.id), { isLocked: false, loginAttempts: 0 }); alert(t('unlocked') || "Unlocked!"); } catch (e) { alert(t('error')); }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (isSubAdmin) return;
    if (!window.confirm(t('confirm_delete') || "Delete?")) return;
    try { await deleteDoc(doc(db, "users", adminId)); } catch (e) { alert(t('error')); }
  };

  if (loading) return <div className="loading-screen">{t('loading')}...</div>;

  return (
    <div className="settings-container">
      <button 
        className="back-btn" 
        onClick={() => navigate(-1)}
        style={{ position: "absolute", top: "30px", left: "30px", zIndex: 100 }}
      >
        ← {t('back')}
      </button>

      <div className="settings-content">
        <img src={bfLogo} alt="BF" className="settings-logo" />
        <h1 className="settings-title">{t('settings')} {isSubAdmin && `(${t('read_only')})`}</h1>

        {/* ================= COMPANY PROFILE ================= */}
        <div className="settings-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="settings-section-title" style={{ margin: 0 }}>{t('company_profile')}</h2>
            {!isSubAdmin && (
              <button 
                className="edit-toggle-btn" 
                onClick={() => isEditingProfile ? handleSaveProfile() : setIsEditingProfile(true)}
                style={{ 
                  background: isEditingProfile ? '#00ff88' : 'rgba(255,217,118,0.1)', 
                  color: isEditingProfile ? '#000' : '#ffd976', 
                  border: isEditingProfile ? 'none' : '1px solid #ffd976', 
                  padding: '6px 15px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' 
                }}
              >
                {isEditingProfile ? <><Check size={16}/> {t('save')}</> : <><Pencil size={16}/> {t('edit_profile')}</>}
              </button>
            )}
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label>{t('contact_person')}</label>
              {isEditingProfile ? 
                <input value={editFormData.name} onChange={e => setEditFormData({...editFormData, name: e.target.value})} className="edit-input" /> : 
                <span>{ownerData?.name || "N/A"}</span>
              }
            </div>

            <div className="info-item">
              <label>{t('company_name')}</label>
              <span style={{ opacity: 0.6 }}>{ownerData?.companyName || "N/A"}</span>
            </div>

            <div className="info-item">
              <label>{t('vat_id')}</label>
              <span style={{ opacity: 0.6 }}>{ownerData?.vatId || "N/A"}</span>
            </div>

            <div className="info-item">
              <label>{t('phone_number')}</label>
              {isEditingProfile ? 
                <input value={editFormData.phone} onChange={e => setEditFormData({...editFormData, phone: e.target.value})} className="edit-input" /> : 
                <span>{ownerData?.phone || "N/A"}</span>
              }
            </div>

            <div className="info-item full-width">
              <label>{t('street_and_number')}</label>
              {isEditingProfile ? 
                <input value={editFormData.address.street} onChange={e => setEditFormData({...editFormData, address: {...editFormData.address, street: e.target.value}})} className="edit-input" /> : 
                <span>{ownerData?.address?.street || "N/A"}</span>
              }
            </div>
            
            <div className="info-item">
              <label>{t('postal_code')}</label>
              {isEditingProfile ? 
                <input value={editFormData.address.zip} onChange={e => setEditFormData({...editFormData, address: {...editFormData.address, zip: e.target.value}})} className="edit-input" /> : 
                <span>{ownerData?.address?.zip || "N/A"}</span>
              }
            </div>

            <div className="info-item">
              <label>{t('city')}</label>
              {isEditingProfile ? 
                <input value={editFormData.address.city} onChange={e => setEditFormData({...editFormData, address: {...editFormData.address, city: e.target.value}})} className="edit-input" /> : 
                <span>{ownerData?.address?.city || "N/A"}</span>
              }
            </div>

            <div className="info-item">
              <label>{t('work_email')} (Owner)</label>
              <span style={{opacity: 0.7}}>{ownerData?.email}</span>
            </div>
          </div>
          {isEditingProfile && <button className="cancel-edit-btn" onClick={() => {setIsEditingProfile(false); setEditFormData(ownerData);}} style={{ marginTop: '15px', background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>{t('cancel_changes')}</button>}
        </div>

        {/* ================= TEAM MANAGEMENT ================= */}
        <div className="settings-card">
          <h2 className="settings-section-title">{t('team_management')}</h2>
          <div className="admin-list">
            <div className="admin-item owner"><strong>{ownerData?.email} (Owner)</strong></div>
            {team.map((member) => (
              <div key={member.id} className="admin-item team-member-row">
                <div className="admin-info">
                  <div className="member-name">{member.name} {member.isLocked && <span style={{color:'#ff4d4d', fontSize:'11px'}}>⚠️ {t('locked').toUpperCase()}</span>}</div>
                  <div className="member-email">{member.email}</div>
                  <div className="member-pw-box">{t('password')}: <span className="pw-text">{isSubAdmin ? "********" : member.plainPassword}</span></div>
                </div>
                {!isSubAdmin && (
                  <div className="admin-actions-flex">
                    {member.isLocked ? 
                      <button className="action-btn unlock" style={{background:'#00ff88'}} onClick={() => handleUnlock(member)}><Unlock size={18} color="#000"/></button> : 
                      <button className="action-btn refresh" onClick={() => handleRefresh(member)}><RefreshCw size={18}/></button>
                    }
                    <button className="action-btn delete" onClick={() => handleDeleteAdmin(member.id)}><Trash2 size={18}/></button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ADD ADMIN FORMA - RODOMA TIK JEI NEPASIEKTAS LIMITAS */}
          {/* Limit logika: Jei turime 1 adminą (savininką), o limitas 1 => 1 < 1 (False) => Nerodom */}
          {/* Limit logika: Jei turime 1 adminą, o limitas 2 => 1 < 2 (True) => Rodom */}
          {!isSubAdmin && (team.length + 1 < currentAdminLimit) && (
            <div className="add-admin-box">
              <h3>{t('add_sub_admin')}</h3>
              <input placeholder={t('name')} value={newAdminName} onChange={e => setNewAdminName(e.target.value)} />
              <input placeholder={t('email')} value={newAdminEmail} onChange={e => setNewAdminEmail(e.target.value)} />
              <button className="create-btn" onClick={handleCreateAdminAccess} disabled={isCreating}>
                  {isCreating ? "Generating..." : t('generate_access')}
              </button>
            </div>
          )}
          
          {/* Informacija jei limitas pasiektas */}
          {!isSubAdmin && (team.length + 1 >= currentAdminLimit) && (
              <div style={{color:'#888', fontSize:'13px', marginTop:'15px', textAlign:'center', borderTop: '1px solid #333', paddingTop: '10px'}}>
                  <p>Limit Reached ({currentAdminLimit} Admins).</p>
                  <p style={{fontSize: '11px', opacity: 0.7}}>Upgrade to add more admins.</p>
              </div>
          )}
        </div>

        <div className="settings-card">
          <h2 className="settings-section-title">{t('plan_and_limits')}</h2>
          <div className="plan-row">
            <span className="status-badge">{ownerData?.subscriptionStatus?.toUpperCase() || "TRIAL"}</span>
            {!isSubAdmin && <button className="upgrade-btn" onClick={() => navigate("/")}>{t('upgrade').toUpperCase()}</button>}
          </div>
          <p>{t('worker_limit')}: {currentWorkerLimit}</p>
          <p>{t('admin_limit')}: {currentAdminLimit}</p>
        </div>

      </div>
    </div>
  );
}

export default AdminSettings;